import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { parseDecimal } from "@/components/atoms/format";

/** Polla periodicamente l'orderbook del primo outcome del market per stimare il mid.
 *  Restituisce probability (0..1) della first outcome. Fallback 0.5.
 *
 *  Il polling si ferma quando la pagina non e' visibile e riprende al ritorno,
 *  con una lettura immediata per non mostrare un prezzo vecchio. Conta: ogni
 *  card monta questo hook, quindi con 50 market in pagina sono 50 richieste
 *  ogni `intervalMs` — su un telefono in tasca significa batteria e traffico
 *  spesi per dati che nessuno sta guardando.
 *
 *  Resta il fatto che le richieste sono una per market: la soluzione vera e'
 *  un endpoint che restituisca i mid in blocco. Vedi il TODO sotto. */
export function useMid(slug: string, intervalMs = 6000): number {
  const [mid, setMid] = useState(0.5);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const fetch = async () => {
      // TODO: sostituire con una GET /v1/markets/mids?slugs=… quando esiste.
      const { data } = await api.GET("/v1/markets/{slug}/orderbook", {
        params: { path: { slug }, query: { depth: 1 } },
      });
      if (cancelled || !data) return;
      const bid = data.bids[0]
        ? parseDecimal(data.bids[0].price)
        : 0;
      const ask = data.asks[0]
        ? parseDecimal(data.asks[0].price)
        : 1;
      if (bid > 0 && ask > 0) setMid((bid + ask) / 2);
      else if (bid > 0) setMid(bid);
      else if (ask > 0 && ask < 1) setMid(ask);
    };

    const start = () => {
      if (timer !== undefined) return;
      timer = setInterval(fetch, intervalMs);
    };
    const stop = () => {
      clearInterval(timer);
      timer = undefined;
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        fetch();
        start();
      }
    };

    if (!document.hidden) {
      fetch();
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [slug, intervalMs]);
  return mid;
}
